import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Bot,
	Building2,
	CheckCircle2,
	ChevronDown,
	Download,
	Edit,
	ExternalLink,
	Eye,
	FileText,
	GitCompareArrows,
	Loader2,
	Package,
	RefreshCw,
	Save,
	Shield,
	Sparkles,
	X,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { PdfFormCompareViewer } from "@/components/pdf-filler/PdfFormCompareViewer";
import { cn, getBidStatusColor } from "@/lib/utils";

// Local types
interface ComplianceCheck {
	id: string;
	title: string;
	description: string | null;
	isRequired: boolean;
	vendorAcknowledged: boolean | null;
	vendorAcknowledgedAt: string | null;
	// Buyer's compliance determination
	isCompliant: boolean | null;
	notes: string;
	checkedBy: string | null;
	checkedAt: string | null;
	// Flag to track if there are pending changes
	isDirty?: boolean;
}


export const Route = createFileRoute("/solicitations/bids/$bidId")({
	component: BidEvaluationPage,
});

function BidEvaluationPage() {
	const { bidId } = Route.useParams();
	const { user, hasPermission } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState("scoring");
	const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
	const [downloadingFormId, setDownloadingFormId] = useState<string | null>(null);
	const [viewingFormId, setViewingFormId] = useState<string | null>(null);
	const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
	const [viewingDocId, setViewingDocId] = useState<string | null>(null);
	const [compareFormId, setCompareFormId] = useState<string | null>(null);
	const [isDownloadingPacket, setIsDownloadingPacket] = useState(false);

	// Fetch bid details including solicitation info
	const { data: bidData, isLoading } = useQuery(
		trpc.vendorManagement.bidSubmissions.getById.queryOptions({
			bidId,
		}),
	);

	const bid = bidData?.bid;
	const solicitationData = bidData?.solicitation;
	const vendorProfile = bidData?.vendorProfile;
	const formSubmissionsData = bidData?.formSubmissions ?? [];
	const docSubmissionsData = bidData?.documentSubmissions ?? [];
	const complianceChecksData = bidData?.complianceChecks ?? [];

	// Mutation for generating filled PDF
	const generateFilledPdfMutation = useMutation(
		trpc.pdfTemplates.generateFilledPdfFromSubmission.mutationOptions(),
	);

	// Mutation for generating bid packet
	const generateBidPacketMutation = useMutation(
		trpc.pdfTemplates.generateBidPacket.mutationOptions(),
	);

	// Determine if solicitation is in "pending award" state
	// Pending award = status is "open" AND due_date is in the past
	const isPendingAward = solicitationData?.status === "open" &&
		solicitationData?.due_date &&
		new Date(solicitationData.due_date) < new Date();

	// Build vendor display info
	// IMPORTANT: Never show the dummy ops+xxx@bidscope.ai email
	const vendor = vendorProfile ? {
		id: vendorProfile.id,
		name: vendorProfile.company ?? (`${vendorProfile.first_name ?? ""} ${vendorProfile.last_name ?? ""}`.trim() || "Unknown Vendor"),
		email: vendorProfile.email ?? "", // Real email from vendor_profiles, empty if not available
		phone: vendorProfile.phone_number ?? "",
	} : {
		id: "unknown",
		name: "Unknown Vendor",
		email: "",
		phone: "",
	};

	// Get evaluation criteria from API (now includes score data and split info)
	const criteria = bidData?.evaluationCriteria ?? [];
	const splitSheetRowNames = bidData?.splitSheetRowNames ?? [];
	const solicitationEvaluators: Array<{ id: string; name: string }> = [];

	// Local state for compliance
	const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([]);

	// Mutation for saving compliance evaluations
	const upsertComplianceEvaluationMutation = useMutation(
		trpc.vendorManagement.bidSubmissions.upsertComplianceEvaluation.mutationOptions(),
	);

	// Mutation for saving criterion scores
	const upsertCriterionScoreMutation = useMutation(
		trpc.vendorManagement.bidSubmissions.upsertCriterionScore.mutationOptions(),
	);

	// Initialize compliance checks from API data when loaded (includes buyer evaluations)
	useEffect(() => {
		if (complianceChecksData.length > 0 && complianceChecks.length === 0) {
			setComplianceChecks(
				complianceChecksData.map((check) => ({
					id: check.id,
					title: check.title,
					description: check.description,
					isRequired: check.isRequired,
					vendorAcknowledged: check.acknowledged,
					vendorAcknowledgedAt: check.acknowledgedAt,
					// Load existing buyer evaluation if present
					isCompliant: check.isCompliant ?? null,
					notes: check.buyerNotes ?? "",
					checkedBy: check.checkedBy ?? null,
					checkedAt: check.checkedAt ?? null,
				}))
			);
		}
	}, [complianceChecksData, complianceChecks.length]);

	// Editing state
	const [editingScoreId, setEditingScoreId] = useState<string | null>(null);
	const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null); // For split criteria
	const [editScoreValue, setEditScoreValue] = useState<number>(0);
	const [editNotesValue, setEditNotesValue] = useState<string>("");

	// Show loading state while fetching data
	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	if (!bid || !solicitationData || !vendor) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Bid not found</h2>
					<p className="text-muted-foreground mt-2">
						The bid you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/solicitations">Back to Solicitations</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	const handleRunAiAnalysis = async () => {
		setIsAiAnalyzing(true);
		// TODO: Implement actual AI analysis
		await new Promise((resolve) => setTimeout(resolve, 2500));
		setIsAiAnalyzing(false);
		toast.success("AI analysis complete!");
	};

	const handleStartEditScore = (criterion: typeof criteria[0], rowIndex?: number) => {
		setEditingScoreId(criterion.id);
		setEditingRowIndex(rowIndex ?? null);

		if (rowIndex !== undefined && criterion.isSplit) {
			// Editing a specific row in a split criterion
			const rowScore = criterion.rowScores?.find(r => r.rowIndex === rowIndex);
			setEditScoreValue(rowScore?.manualScore ?? rowScore?.aiScore ?? 0);
			setEditNotesValue(rowScore?.manualNotes ?? "");
		} else {
			// Editing bid-wide score
			setEditScoreValue(criterion.manualScore ?? criterion.aiScore ?? 0);
			setEditNotesValue(criterion.manualNotes ?? "");
		}
	};

	const handleSaveScore = async (criterionId: string, maxPoints: number) => {
		// Validate score bounds
		if (editScoreValue < 0) {
			toast.error("Score cannot be less than 0");
			return;
		}
		if (editScoreValue > maxPoints) {
			toast.error(`Score cannot exceed ${maxPoints} points`);
			return;
		}

		try {
			await upsertCriterionScoreMutation.mutateAsync({
				vendorBidId: bidId,
				solicitationEvaluationCriterionId: criterionId,
				rowIndex: editingRowIndex,
				manualScore: editScoreValue,
				manualNotes: editNotesValue || undefined,
				maxPoints,
			});
			// Refresh the data to get updated scores
			await queryClient.invalidateQueries({
				queryKey: trpc.vendorManagement.bidSubmissions.getById.queryKey({ bidId }),
			});
			setEditingScoreId(null);
			setEditingRowIndex(null);
			toast.success("Score saved");
		} catch (error) {
			toast.error("Failed to save score");
		}
	};

	const handleCancelEditScore = () => {
		setEditingScoreId(null);
		setEditingRowIndex(null);
		setEditScoreValue(0);
		setEditNotesValue("");
	};

	const handleComplianceChange = async (
		checkId: string,
		isCompliant: boolean | null,
	) => {
		// Update local state immediately for responsive UI
		const currentCheck = complianceChecks.find((c) => c.id === checkId);
		setComplianceChecks((prev) =>
			prev.map((c) =>
				c.id === checkId
					? {
							...c,
							isCompliant,
							checkedBy:
								isCompliant !== null
									? `${user?.firstName} ${user?.lastName}`
									: null,
							checkedAt: isCompliant !== null ? new Date().toISOString() : null,
						}
					: c,
			),
		);

		// Persist to database
		try {
			await upsertComplianceEvaluationMutation.mutateAsync({
				vendorBidId: bidId,
				solicitationComplianceCheckId: checkId,
				isCompliant,
				notes: currentCheck?.notes ?? "",
			});
			toast.success("Compliance status saved");
		} catch (error) {
			toast.error("Failed to save compliance status");
			// Revert on error
			setComplianceChecks((prev) =>
				prev.map((c) =>
					c.id === checkId
						? {
								...c,
								isCompliant: currentCheck?.isCompliant ?? null,
								checkedBy: currentCheck?.checkedBy ?? null,
								checkedAt: currentCheck?.checkedAt ?? null,
							}
						: c,
				),
			);
		}
	};

	const handleComplianceNotesChange = (checkId: string, notes: string) => {
		setComplianceChecks((prev) =>
			prev.map((c) => (c.id === checkId ? { ...c, notes, isDirty: true } : c)),
		);
	};

	// Save compliance notes on blur (when user finishes editing)
	const handleComplianceNotesBlur = async (checkId: string) => {
		const check = complianceChecks.find((c) => c.id === checkId);
		if (!check || !check.isDirty) return;

		try {
			await upsertComplianceEvaluationMutation.mutateAsync({
				vendorBidId: bidId,
				solicitationComplianceCheckId: checkId,
				isCompliant: check.isCompliant,
				notes: check.notes,
			});
			// Clear dirty flag on success
			setComplianceChecks((prev) =>
				prev.map((c) => (c.id === checkId ? { ...c, isDirty: false } : c)),
			);
			toast.success("Notes saved");
		} catch (error) {
			toast.error("Failed to save notes");
		}
	};

	// View handler for form PDFs - opens in new tab
	const handleViewFormPdf = async (submissionId: string) => {
		setViewingFormId(submissionId);
		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank");
			} else {
				toast.error("Failed to generate PDF for viewing");
			}
		} catch (error) {
			console.error("View error:", error);
			toast.error("Failed to view form PDF");
		} finally {
			setViewingFormId(null);
		}
	};

	// Download handler for form PDFs - generates PDF with field values applied
	const handleDownloadFormPdf = async (submissionId: string, title: string) => {
		setDownloadingFormId(submissionId);
		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });

				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = result.filename || `${title} - Filled.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to generate filled PDF");
			}
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download form PDF");
		} finally {
			setDownloadingFormId(null);
		}
	};

	// View handler for uploaded documents - opens in new tab
	const handleViewDocument = async (docId: string, filePath: string) => {
		setViewingDocId(docId);
		try {
			const result = await queryClient.fetchQuery(
				trpc.vendorManagement.vendorSubmissions.getDocumentDownloadUrl.queryOptions({ filePath })
			);
			if (result?.signedUrl) {
				window.open(result.signedUrl, "_blank");
			} else {
				toast.error("Failed to get document URL");
			}
		} catch (error) {
			toast.error("Failed to view document");
		} finally {
			setViewingDocId(null);
		}
	};

	// Download handler for uploaded documents - triggers actual download
	const handleDownloadDocument = async (docId: string, filePath: string, filename: string) => {
		setDownloadingDocId(docId);
		try {
			const result = await queryClient.fetchQuery(
				trpc.vendorManagement.vendorSubmissions.getDocumentDownloadUrl.queryOptions({ filePath })
			);
			if (result?.signedUrl) {
				const response = await fetch(result.signedUrl);
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to get download URL");
			}
		} catch (error) {
			toast.error("Failed to download document");
		} finally {
			setDownloadingDocId(null);
		}
	};

	// Helper to check if file is a PDF
	const isPdfFile = (filePath: string) => filePath.toLowerCase().endsWith(".pdf");

	// Download bid packet handler
	const handleDownloadPacket = async (includeOnboarding: boolean) => {
		setIsDownloadingPacket(true);
		try {
			const result = await generateBidPacketMutation.mutateAsync({
				vendorBidId: bidId,
				includeOnboarding,
			});

			if (result?.pdfBase64) {
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });

				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = result.filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to generate bid packet");
			}
		} catch (error) {
			console.error("Download packet error:", error);
			toast.error("Failed to download bid packet");
		} finally {
			setIsDownloadingPacket(false);
		}
	};

	// Calculate total scores from criteria (which now include score data from API)
	// For split criteria, average the row scores
	const totalAiScore = criteria.reduce((sum, c) => sum + (c.aiScore ?? 0), 0);
	const totalManualScore = criteria.reduce((sum, c) => {
		if (c.isSplit && c.rowScores && c.rowScores.length > 0) {
			// Average of row scores for split criteria
			const avgRowScore =
				c.rowScores.reduce((s, r) => s + (r.manualScore ?? 0), 0) /
				c.rowScores.length;
			return sum + avgRowScore;
		}
		return sum + (c.manualScore ?? c.aiScore ?? 0);
	}, 0);
	const maxPossibleScore = criteria.reduce((sum, c) => sum + c.maxPoints, 0);

	// Check if we have split evaluation
	const hasSplitCriteria = criteria.some((c) => c.isSplit);

	// Calculate per-row scores when split evaluation is configured
	const rowScores = hasSplitCriteria && splitSheetRowNames.length > 0
		? splitSheetRowNames.map((rowName, rowIndex) => {
				let rowTotal = 0;
				let rowMax = 0;

				for (const c of criteria) {
					if (c.isSplit) {
						// Use the score for this specific row
						const rowScore = c.rowScores?.find((r) => r.rowIndex === rowIndex);
						rowTotal += rowScore?.manualScore ?? 0;
						rowMax += c.maxPoints;
					} else {
						// Non-split criteria contribute equally to each row
						rowTotal += c.manualScore ?? c.aiScore ?? 0;
						rowMax += c.maxPoints;
					}
				}

				const percentage = rowMax > 0 ? Math.round((rowTotal / rowMax) * 100) : 0;
				return {
					rowName,
					rowIndex,
					score: rowTotal,
					maxScore: rowMax,
					percentage,
				};
			})
		: [];

	// Calculate compliance status
	const compliantCount = complianceChecks.filter(
		(c) => c.isCompliant === true,
	).length;
	const nonCompliantCount = complianceChecks.filter(
		(c) => c.isCompliant === false,
	).length;
	const pendingComplianceCount = complianceChecks.filter(
		(c) => c.isCompliant === null,
	).length;

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/solicitations/$id" params={{ id: solicitationData.id }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge className={getBidStatusColor(bid.status)}>
								{bid.status.replace("_", " ")}
							</Badge>
						</div>
						<h1 className="text-3xl font-bold tracking-tight">
							Bid Evaluation: {vendor.name}
						</h1>
						<p className="text-muted-foreground">{solicitationData.title}</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button disabled={isDownloadingPacket}>
								{isDownloadingPacket ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Package className="mr-2 h-4 w-4" />
								)}
								Download Packet
								<ChevronDown className="ml-2 h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => handleDownloadPacket(false)}>
								<FileText className="mr-2 h-4 w-4" />
								Download Bid Packet
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => handleDownloadPacket(true)}>
								<Package className="mr-2 h-4 w-4" />
								Download Onboarding + Bid Packet
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{/* Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="scoring">Scoring</TabsTrigger>
						<TabsTrigger value="compliance">Compliance<span className="sr-only"> tab</span></TabsTrigger>
						<TabsTrigger value="documents">Documents</TabsTrigger>
						<TabsTrigger value="vendor">Vendor Info</TabsTrigger>
					</TabsList>

					<TabsContent value="scoring" className="space-y-6">
						{/* AI Analysis Controls */}
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle className="flex items-center gap-2">
										<Sparkles className="h-5 w-5" />
										AI-Assisted Scoring
									</CardTitle>
									<CardDescription>
										AI analyzes the bid against evaluation criteria and suggests
										scores.
									</CardDescription>
								</div>
								<div className="flex flex-col items-end gap-1">
									<Button
										onClick={handleRunAiAnalysis}
										disabled={true}
									>
										<RefreshCw className="mr-2 h-4 w-4" />
										Run AI Analysis
									</Button>
									<p className="text-xs text-muted-foreground">
										AI assisted scoring is disabled for this solicitation.
									</p>
								</div>
							</CardHeader>
						</Card>

						{/* Score by Criterion */}
						<Card>
							<CardHeader>
								<CardTitle>Evaluation Scores</CardTitle>
								<CardDescription>
									Review AI suggestions and provide final scores for each
									criterion.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{criteria.map((criterion) => {
									const isEditingThisCriterion = editingScoreId === criterion.id;

									return (
										<div key={criterion.id} className="border rounded-lg p-4">
											<div className="flex items-start justify-between mb-3">
												<div>
													<h4 className="font-medium">
														{criterion.title}
														{criterion.isSplit && (
															<Badge variant="secondary" className="ml-2 text-xs">
																Per Line Item
															</Badge>
														)}
													</h4>
													<p className="text-sm text-muted-foreground">
														{criterion.description}
													</p>
												</div>
												<Badge variant="outline">
													{criterion.maxPoints} pts max
												</Badge>
											</div>

											<div className="space-y-4">
												{/* AI Score - only show if available (for non-split criteria) */}
												{!criterion.isSplit && criterion.aiScore !== null && (
													<div className="bg-blue-50 p-3 rounded-lg">
														<div className="flex items-center gap-2 mb-2">
															<Bot className="h-4 w-4 text-blue-600" />
															<span className="text-sm font-medium text-blue-900">
																AI Suggestion: {criterion.aiScore} /{" "}
																{criterion.maxPoints}
															</span>
														</div>
														{criterion.aiNotes && (
															<p className="text-sm text-blue-800">
																{criterion.aiNotes}
															</p>
														)}
													</div>
												)}

												{/* Split Criteria - Table with per-row scores */}
												{criterion.isSplit && splitSheetRowNames.length > 0 ? (
													<div className="border rounded-lg overflow-hidden">
														<table className="w-full text-sm">
															<thead className="bg-muted/50">
																<tr>
																	<th className="text-left py-2 px-3 font-medium">Line Item</th>
																	<th className="text-left py-2 px-3 font-medium w-28">Score</th>
																	<th className="text-left py-2 px-3 font-medium">Notes</th>
																	<th className="text-right py-2 px-3 font-medium w-24">Actions</th>
																</tr>
															</thead>
															<tbody>
																{splitSheetRowNames.map((rowName, rowIndex) => {
																	const rowScore = criterion.rowScores?.find(
																		(r) => r.rowIndex === rowIndex
																	);
																	const isEditingThisRow =
																		isEditingThisCriterion && editingRowIndex === rowIndex;

																	return (
																		<tr key={rowIndex} className="border-t">
																			<td className="py-2 px-3 font-medium">
																				{rowName}
																			</td>
																			{isEditingThisRow ? (
																				<>
																					<td className="py-2 px-3">
																						<Input
																							type="number"
																							min={0}
																							max={criterion.maxPoints}
																							value={editScoreValue}
																							onChange={(e) =>
																								setEditScoreValue(Number(e.target.value))
																							}
																							className="w-20 h-8"
																						/>
																					</td>
																					<td className="py-2 px-3">
																						<Input
																							value={editNotesValue}
																							onChange={(e) =>
																								setEditNotesValue(e.target.value)
																							}
																							placeholder="Notes..."
																							className="h-8"
																						/>
																					</td>
																					<td className="py-2 px-3 text-right">
																						<div className="flex justify-end gap-1">
																							<Button
																								variant="ghost"
																								size="sm"
																								className="h-7 px-2"
																								onClick={handleCancelEditScore}
																							>
																								<X className="h-3 w-3" />
																							</Button>
																							<Button
																								size="sm"
																								className="h-7 px-2"
																								onClick={() =>
																									handleSaveScore(
																										criterion.id,
																										criterion.maxPoints
																									)
																								}
																								disabled={upsertCriterionScoreMutation.isPending}
																							>
																								{upsertCriterionScoreMutation.isPending ? (
																									<Loader2 className="h-3 w-3 animate-spin" />
																								) : (
																									<Save className="h-3 w-3" />
																								)}
																							</Button>
																						</div>
																					</td>
																				</>
																			) : (
																				<>
																					<td className="py-2 px-3">
																						{rowScore?.manualScore != null ? (
																							<span className="flex items-center gap-1">
																								<CheckCircle2 className="h-3 w-3 text-green-600" />
																								{rowScore.manualScore} / {criterion.maxPoints}
																							</span>
																						) : (
																							<span className="text-muted-foreground">—</span>
																						)}
																					</td>
																					<td className="py-2 px-3 text-muted-foreground">
																						{rowScore?.manualNotes || "—"}
																					</td>
																					<td className="py-2 px-3 text-right">
																						{hasPermission("edit") && (
																							<Button
																								variant="ghost"
																								size="sm"
																								className="h-7 px-2"
																								onClick={() =>
																									handleStartEditScore(criterion, rowIndex)
																								}
																							>
																								<Edit className="h-3 w-3" />
																							</Button>
																						)}
																					</td>
																				</>
																			)}
																		</tr>
																	);
																})}
															</tbody>
														</table>
														{/* Summary row */}
														<div className="border-t bg-muted/30 py-2 px-3 flex justify-between items-center text-sm">
															<span className="font-medium">Average Score:</span>
															<span>
																{criterion.rowScores && criterion.rowScores.length > 0
																	? `${(
																			criterion.rowScores.reduce(
																				(sum, r) => sum + (r.manualScore ?? 0),
																				0
																			) / criterion.rowScores.length
																		).toFixed(1)} / ${criterion.maxPoints}`
																	: "Not scored"}
															</span>
														</div>
													</div>
												) : !criterion.isSplit ? (
													/* Non-split criteria - single score editor */
													isEditingThisCriterion && editingRowIndex === null ? (
														<div className="space-y-3 bg-muted/50 p-3 rounded-lg">
															<div className="flex items-center gap-4">
																<div className="space-y-1">
																	<Label htmlFor="score-edit-input">Your Score (0-{criterion.maxPoints})</Label>
																	<Input
																		id="score-edit-input"
																		type="number"
																		min={0}
																		max={criterion.maxPoints}
																		value={editScoreValue}
																		onChange={(e) =>
																			setEditScoreValue(Number(e.target.value))
																		}
																		className="w-24"
																	/>
																</div>
																<div className="flex-1 space-y-1">
																	<Label>Notes</Label>
																	<Textarea
																		value={editNotesValue}
																		onChange={(e) =>
																			setEditNotesValue(e.target.value)
																		}
																		placeholder="Add your evaluation notes..."
																		rows={2}
																	/>
																</div>
															</div>
															<div className="flex justify-end gap-2">
																<Button
																	variant="outline"
																	size="sm"
																	onClick={handleCancelEditScore}
																>
																	<X className="mr-1 h-3 w-3" />
																	Cancel
																</Button>
																<Button
																	size="sm"
																	onClick={() => handleSaveScore(criterion.id, criterion.maxPoints)}
																	disabled={upsertCriterionScoreMutation.isPending}
																>
																	{upsertCriterionScoreMutation.isPending ? (
																		<Loader2 className="mr-1 h-3 w-3 animate-spin" />
																	) : (
																		<Save className="mr-1 h-3 w-3" />
																	)}
																	Save
																</Button>
															</div>
														</div>
													) : (
														<div className="space-y-1">
															<div className="flex items-center justify-between">
																<div>
																	{criterion.manualScore !== null ? (
																		<div className="flex items-center gap-2">
																			<CheckCircle2 className="h-4 w-4 text-green-600" />
																			<span className="font-medium">
																				Final Score: {criterion.manualScore} /{" "}
																				{criterion.maxPoints}
																			</span>
																			{criterion.manualNotes && (
																				<span className="text-sm text-muted-foreground">
																					- {criterion.manualNotes}
																				</span>
																			)}
																		</div>
																	) : (
																		<span className="text-sm text-muted-foreground">
																			Not yet reviewed
																		</span>
																	)}
																</div>
																{hasPermission("edit") && (
																	<Button
																		variant="outline"
																		size="sm"
																		onClick={() => handleStartEditScore(criterion)}
																	>
																		<Edit className="mr-1 h-3 w-3" />
																		{criterion.manualScore !== null
																			? "Edit"
																			: "Score"}
																	</Button>
																)}
															</div>
															{criterion.reviewedAt && (
																<p className="text-xs text-muted-foreground">
																	Last updated{criterion.reviewedBy ? ` by ${criterion.reviewedBy}` : ""} on{" "}
																	{new Date(
																		criterion.reviewedAt,
																	).toLocaleDateString()}
																</p>
															)}
														</div>
													)
												) : (
													<p className="text-sm text-muted-foreground">
														No line items configured for split scoring.
													</p>
												)}
											</div>
										</div>
									);
								})}

								{criteria.length === 0 && (
									<p className="text-center py-8 text-muted-foreground">
										No evaluation criteria defined for this solicitation.
									</p>
								)}
							</CardContent>
						</Card>

						{/* Score Summary */}
						<Card>
							<CardHeader>
								<CardTitle>Score Summary</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									{/* Overall scores (shown when no split evaluation or as header) */}
									{!hasSplitCriteria && (
										<>
											<div>
												<div className="flex justify-between mb-1">
													<span className="text-sm">AI Score</span>
													<span className="text-sm font-medium">
														{totalAiScore} / {maxPossibleScore}
														{maxPossibleScore > 0 && ` (${Math.round((totalAiScore / maxPossibleScore) * 100)}%)`}
													</span>
												</div>
												<Progress value={maxPossibleScore > 0 ? (totalAiScore / maxPossibleScore) * 100 : 0} />
											</div>
											<div>
												<div className="flex justify-between mb-1">
													<span className="text-sm">Final Score</span>
													<span className="text-sm font-medium">
														{totalManualScore} / {maxPossibleScore}
														{maxPossibleScore > 0 && ` (${Math.round((totalManualScore / maxPossibleScore) * 100)}%)`}
													</span>
												</div>
												<Progress
													value={maxPossibleScore > 0 ? (totalManualScore / maxPossibleScore) * 100 : 0}
													className="[&>div]:bg-green-500"
												/>
											</div>
										</>
									)}

									{/* Per-row scores when split evaluation is configured */}
									{hasSplitCriteria && rowScores.length > 0 && (
										<div className="space-y-3">
											<p className="text-sm text-muted-foreground mb-2">
												Scores by line item:
											</p>
											<div className="border rounded-lg overflow-hidden">
												<Table>
													<TableHeader>
														<TableRow className="bg-muted/50">
															<TableHead>Line Item</TableHead>
															<TableHead className="text-right">Score</TableHead>
															<TableHead className="text-right w-[100px]">%</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{rowScores.map((row) => (
															<TableRow key={row.rowIndex}>
																<TableCell className="font-medium">
																	{row.rowName}
																</TableCell>
																<TableCell className="text-right">
																	{row.score} / {row.maxScore}
																</TableCell>
																<TableCell className="text-right">
																	<span
																		className={cn(
																			"font-medium",
																			row.percentage >= 70
																				? "text-green-600"
																				: row.percentage >= 50
																					? "text-yellow-600"
																					: "text-red-600",
																		)}
																	>
																		{row.percentage}%
																	</span>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</div>
											{/* Average score */}
											<div className="pt-2 border-t">
												<div className="flex justify-between mb-1">
													<span className="text-sm font-medium">Average Score</span>
													<span className="text-sm font-medium">
														{rowScores.length > 0
															? Math.round(
																	rowScores.reduce((sum, r) => sum + r.percentage, 0) /
																		rowScores.length,
																)
															: 0}
														%
													</span>
												</div>
												<Progress
													value={
														rowScores.length > 0
															? rowScores.reduce((sum, r) => sum + r.percentage, 0) /
																rowScores.length
															: 0
													}
													className="[&>div]:bg-green-500"
												/>
											</div>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="compliance" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Shield className="h-5 w-5" />
									Compliance Checklist
								</CardTitle>
								<CardDescription>
									Verify that the bid meets all required criteria and
									documentation.
								</CardDescription>
							</CardHeader>
							<CardContent>
								{/* Status Summary */}
								<div className="flex gap-4 mb-6">
									<Badge className="bg-green-100 text-green-700">
										<CheckCircle2 className="mr-1 h-3 w-3" />
										{compliantCount} Compliant
									</Badge>
									<Badge className="bg-red-100 text-red-700">
										<XCircle className="mr-1 h-3 w-3" />
										{nonCompliantCount} Non-Compliant
									</Badge>
									<Badge className="bg-yellow-100 text-yellow-700">
										<AlertCircle className="mr-1 h-3 w-3" />
										{pendingComplianceCount} Pending
									</Badge>
								</div>

								{/* Compliance Items */}
								<div className="space-y-4">
									{complianceChecks.map((check) => (
										<div
											key={check.id}
											className="flex items-start gap-4 p-4 border rounded-lg"
										>
											<div className="flex flex-col gap-2">
												<Button
													variant={
														check.isCompliant === true ? "default" : "outline"
													}
													size="sm"
													className="w-8 h-8 p-0"
													onClick={() =>
														handleComplianceChange(
															check.id,
															check.isCompliant === true ? null : true,
														)
													}
												>
													<CheckCircle2 className="h-4 w-4" />
												</Button>
												<Button
													variant={
														check.isCompliant === false
															? "destructive"
															: "outline"
													}
													size="sm"
													className="w-8 h-8 p-0"
													onClick={() =>
														handleComplianceChange(
															check.id,
															check.isCompliant === false ? null : false,
														)
													}
												>
													<XCircle className="h-4 w-4" />
												</Button>
											</div>
											<div className="flex-1">
												<div className="flex items-start justify-between">
													<div>
														<p className="font-medium">
															{check.title}
															{check.isRequired && (
																<span className="text-destructive ml-1">*</span>
															)}
														</p>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
													<Badge
														variant="outline"
														className={
															check.vendorAcknowledged === true
																? "bg-green-50 text-green-700 border-green-200"
																: check.vendorAcknowledged === false
																	? "bg-red-50 text-red-700 border-red-200"
																	: "bg-yellow-50 text-yellow-700 border-yellow-200"
														}
													>
														{check.vendorAcknowledged === true
															? "Vendor Acknowledged"
															: check.vendorAcknowledged === false
																? "Not Acknowledged"
																: "Pending"}
													</Badge>
												</div>
												<Textarea
													placeholder="Add notes..."
													value={check.notes}
													onChange={(e) =>
														handleComplianceNotesChange(
															check.id,
															e.target.value,
														)
													}
													onBlur={() => handleComplianceNotesBlur(check.id)}
													className="mt-2"
													rows={2}
												/>
												{check.checkedBy && (
													<p className="text-xs text-muted-foreground mt-2">
														Checked by {check.checkedBy} on{" "}
														{check.checkedAt
															? new Date(check.checkedAt).toLocaleDateString()
															: ""}
													</p>
												)}
											</div>
										</div>
									))}

									{complianceChecks.length === 0 && (
										<p className="text-center py-8 text-muted-foreground">
											No compliance checks have been configured for this solicitation.
										</p>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="documents" className="space-y-6">
						{/* Completed Forms */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<FileText className="h-5 w-5" />
									Completed Forms
								</CardTitle>
								<CardDescription>
									Forms submitted with this bid. Download to view filled responses.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{formSubmissionsData.length === 0 ? (
										<p className="text-center py-8 text-muted-foreground">
											No forms have been submitted with this bid.
										</p>
									) : (
										formSubmissionsData.map((submission) => (
											<div
												key={submission.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">
															{submission.form_title ?? "Form"}
														</p>
														<p className="text-sm text-muted-foreground">
															Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
															{submission.is_signed && " • Signed"}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-1">
													{submission.solicitation_form_id && (
														<Button
															variant="outline"
															size="sm"
															onClick={() => setCompareFormId(submission.solicitation_form_id)}
														>
															<GitCompareArrows className="h-4 w-4 mr-1" />
															Compare
														</Button>
													)}
													<Button
														variant="outline"
														size="sm"
														disabled={viewingFormId === submission.id}
														onClick={() => handleViewFormPdf(submission.id)}
													>
														{viewingFormId === submission.id ? (
															<Loader2 className="h-4 w-4 mr-1 animate-spin" />
														) : (
															<Eye className="h-4 w-4 mr-1" />
														)}
														View
													</Button>
													<Button
														variant="outline"
														size="sm"
														disabled={downloadingFormId === submission.id}
														onClick={() => handleDownloadFormPdf(submission.id, submission.form_title ?? "Form")}
													>
														{downloadingFormId === submission.id ? (
															<Loader2 className="h-4 w-4 mr-1 animate-spin" />
														) : (
															<Download className="h-4 w-4 mr-1" />
														)}
														Download
													</Button>
												</div>
											</div>
										))
									)}
								</div>
							</CardContent>
						</Card>

						{/* Uploaded Documents */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<FileText className="h-5 w-5" />
									Uploaded Documents
								</CardTitle>
								<CardDescription>
									Required documents uploaded by the vendor.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{docSubmissionsData.length === 0 ? (
										<p className="text-center py-8 text-muted-foreground">
											No documents have been uploaded with this bid.
										</p>
									) : (
										docSubmissionsData.map((doc) => (
											<div
												key={doc.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">
															{doc.document_title ?? doc.original_filename ?? "Document"}
														</p>
														<p className="text-sm text-muted-foreground">
															Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
															{doc.original_filename && ` • ${doc.original_filename}`}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-1">
													{isPdfFile(doc.file_path) && (
														<Button
															variant="outline"
															size="sm"
															disabled={viewingDocId === doc.id}
															onClick={() => handleViewDocument(doc.id, doc.file_path)}
														>
															{viewingDocId === doc.id ? (
																<Loader2 className="h-4 w-4 mr-1 animate-spin" />
															) : (
																<Eye className="h-4 w-4 mr-1" />
															)}
															View
														</Button>
													)}
													<Button
														variant="outline"
														size="sm"
														disabled={downloadingDocId === doc.id}
														onClick={() => handleDownloadDocument(doc.id, doc.file_path, doc.original_filename ?? doc.document_title ?? "document")}
													>
														{downloadingDocId === doc.id ? (
															<Loader2 className="h-4 w-4 mr-1 animate-spin" />
														) : (
															<Download className="h-4 w-4 mr-1" />
														)}
														Download
													</Button>
												</div>
											</div>
										))
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="vendor" className="space-y-4">
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<CardTitle className="flex items-center gap-2">
									<Building2 className="h-5 w-5" />
									Vendor Information
								</CardTitle>
								{vendorProfile && (
									<Button variant="outline" size="sm" asChild>
										<a
											href={`/vendors/${vendorProfile.id}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											View Full Profile
											<ExternalLink className="ml-1 h-3 w-3" />
										</a>
									</Button>
								)}
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div>
										<Label className="text-muted-foreground">
											Company Name
										</Label>
										<p className="font-medium">{vendor.name}</p>
									</div>
									{vendorProfile && (
										<div>
											<Label className="text-muted-foreground">
												Contact Name
											</Label>
											<p className="font-medium">
												{`${vendorProfile.first_name ?? ""} ${vendorProfile.last_name ?? ""}`.trim() || "—"}
											</p>
										</div>
									)}
									{vendor.email && (
										<div>
											<Label className="text-muted-foreground">Email</Label>
											<p className="font-medium">
												<a href={`mailto:${vendor.email}`} className="text-primary hover:underline">
													{vendor.email}
												</a>
											</p>
										</div>
									)}
									{vendor.phone && (
										<div>
											<Label className="text-muted-foreground">Phone</Label>
											<p className="font-medium">
												<a href={`tel:${vendor.phone}`} className="text-primary hover:underline">
													{vendor.phone}
												</a>
											</p>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			</div>

			{/* Form Comparison Modal */}
			{compareFormId && (
				<PdfFormCompareViewer
					open={!!compareFormId}
					onClose={() => setCompareFormId(null)}
					solicitationFormId={compareFormId}
					currentVendorEmail={bid?.vendor_email}
				/>
			)}
		</DashboardLayout>
	);
}
